import {gql} from "@apollo/client"
import {graph, GraphResponse} from "config/axios-context"
import {IdName} from "./common-types"
import {
    DeletePersonTagMutation,
    DeletePersonTagMutationVariables,
    GetPersonTagQuery,
    GetPersonTagQueryVariables,
    PersonFilterInput,
    PersonListInfoQuery,
    PersonListInfoQueryVariables,
    PersonQuery,
    PersonQueryVariables,
    TagMutation,
    TagMutationVariables
} from "./graphql"
import {INotification} from "./notification-type"

// #region types
export type ContactType = 'email' | 'tel'

export type PersonContactType = {
    id: number | string
    type: ContactType,
    value: string,
    link?: string
}

export type PersonTagLinkType = {
    id: number | string,
    tagId: number | string,
    name: string
};

export type PersonType = {
    id: number
    name: string,
    contactInfo: PersonContactType[],
    note?: string | null,
    tags: PersonTagLinkType[],
    countryCode?: string | null,
    country?: {
        name: string,
        flag: string,
        alpha3Code: string,
        translations?: [
            {
                name: string,
                language: string
            }
        ] | null,
        translate?: string
    } | null
}
// #endregion 

// #region graph
const personQuery = gql`
query person($id: Int) {
    person(where: {id: {eq: $id}}) {
      id,
      name,
      note,
      contactInfo {
        items {
          value,
          link,
          name,
          id,
          personId
        }
      }
      tags {
        items {
          id
          tag {
            id
            name
          }
        }
      } 
      countryCode
      country {
        alpha3Code
        name
        flag
      }
    }
  }
`;

export const getPersonData = async (id: number) => {
    try {
        const response = await graph<PersonQuery, PersonQueryVariables>({
            query: personQuery,
            variables: {
                id
            }
        });

        return response;
    } catch (error) {
        throw error;
    }
}

export const PersonQueryResponseToPersonType = (response: GraphResponse<PersonQuery>): PersonType | null => {
    if (response.data?.person) {
        const person: PersonType = {
            name: response.data.person.name,
            id: response.data.person.id,
            note: response.data.person.note,
            contactInfo: response.data.person.contactInfo?.items?.map(c => {
                return {
                    id: c!.id,
                    type: c!.name,
                    value: c!.value,
                    link: c!.link
                } as PersonContactType;
            }) || [],
            tags: response.data.person.tags?.items?.map(t => {
                return {
                    id: t.id,
                    tagId: t.tag.id,
                    name: t.tag.name
                };
            }) || [],
            countryCode: response.data.person.countryCode,
            country: response.data.person.country
        };

        return person;
    }

    return null;
}

const personListInfoQuery = gql`
query personListInfo($where: PersonFilterInput, $skip: Int, $take: Int, $order:[PersonSortInput!]) {
    people {
      items(where:$where, take:$take skip:$skip, order:$order) {
        id,
        name,
        note,
        contactInfo {
          items {
            value,
            name,
            id,
            link
          }
        }
        tags {
          items {
            id
            tag {
              id
              name
            }
          }
        }
        countryCode
        country {
          alpha3Code
          name
          flag
        }
      },
      count(where:$where)
    }
  }
`;

export const personListInfo = async (variables?: PersonListInfoQueryVariables) => {
    const response = await graph<PersonListInfoQuery, PersonListInfoQueryVariables>({
        query: personListInfoQuery,
        variables: variables
    });

    const people: { items: PersonType[], count: number } = {
        items: [],
        count: 0
    };

    if (response.success && response.data?.people?.items) {
        people.items = response.data?.people?.items?.map(p => {
            const per: PersonType = {
                name: p!.name,
                id: p!.id,
                note: p!.note,
                contactInfo: p!.contactInfo?.items?.map(c => {
                    return {
                        id: c!.id,
                        type: c!.name,
                        value: c!.value,
                        link: c!.link
                    } as PersonContactType
                }) || [],
                tags: p.tags.items.map(t => {
                    return {
                        id: t.id,
                        tagId: t.tag.id,
                        name: t.tag.name
                    };
                }) || [],
                country: p.country,
                countryCode: p.countryCode
            };

            return per;
        });
        people.count = response.data!.people!.count ?? 0;
    }

    return {
        data: people,
        response: response
    };
}
// #endregion graph

// #region delete
const personDeleteMutation = gql`
mutation DeletePerson($id: Int, $where: PersonFilterInput) {
    personDelete(id:$id, where:$where) 
  }
`;

export const deletePerson = (condition: number | PersonFilterInput) => {
    // TODO: implement delete
    throw new Error("Not implemented");
}
// #endregion delete


// #region save
const savePerson = gql`
mutation savePerson($person: PersonInput!) {
    person(model: $person) {
       id,
       name,
       note,
       contactInfo {
         items {
           value,
           link,
           name,
           id,
           personId
         }
       },
       countryCode
     }
   }
`
// #endregion save


// #region tags
const saveTagMutation = gql`
mutation Tag($model: PersonTagInput!) {
  personTag(model: $model) {
    id
  }
}`;

export const saveTag = async (model: IdName, t: (type: INotification['type'], message: string, description?: string) => void) => {
    try {
        const request = await graph<TagMutation, TagMutationVariables>({
            query: saveTagMutation,
            variables: {
                model
            }
        });

        if (request.errors) {
            throw request.errors;
        }

        return request.data!.personTag!.id;
    } catch (error: any) {
        t('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
        throw error;
    }
}

const deleteTagMutation = gql`
mutation DeletePersonTag($id: Int, $where: PersonTagFilterInput) {
  personTagDelete(id: $id, where: $where)
}`

export const deleteTag = async (id: number, t: (type: INotification['type'], message: string, description?: string) => void) => {
    try {
        const request = await graph<DeletePersonTagMutation, DeletePersonTagMutationVariables>({
            query: deleteTagMutation,
            variables: {
                id
            }
        });

        if (request.errors) {
            throw request.errors;
        }
    } catch (error: any) {
        t('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
        throw error;
    }
}

const getTagQuery = gql`
query GetPersonTag($id: Int, $where: PersonTagFilterInput) {
  personTag(id: $id, where: $where) {
   name,
   id 
  }
}`

export const getTag = async (condition: GetPersonTagQueryVariables, t: (type: INotification['type'], message: string, description?: string) => void) => {
    try {
        const request = await graph<GetPersonTagQuery, GetPersonTagQueryVariables>({
            query: getTagQuery,
            variables: condition
        });

        if (request.errors) {
            throw request.errors;
        }

        return request.data!.personTag;
    } catch (error: any) {
        t('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
        throw error;
    }
}
// #endregion tags
